import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useHomeIQ } from "@/homeiq/state/HomeIQContext";
import { buildSnapshot } from "@/homeiq/services/reportSnapshot";
import { generateReport, reportPdfUrl } from "@/homeiq/services/reportClient";

const EASE = [0.16, 1, 0.3, 1];

// Staged progress messages during report generation. The backend call is a
// single HTTP round-trip of ~18s (Playwright cold-launch + full-page render
// + Mongo persist + optional email). Rather than showing a single opaque
// spinner label, we walk the user through what's actually happening on the
// server. Timings tuned to real observed durations in preview.
const REPORT_STAGES = [
    { at: 0,     label: "Assembling your snapshot" },
    { at: 1500,  label: "Composing your 10-page report" },
    { at: 6000,  label: "Rendering pages" },
    { at: 12000, label: "Preparing download" },
    { at: 20000, label: "Almost there — final touches" },
];

// ──────────────────────────────────────────────────────────────────
// HomeIQ Report — Lead Capture Modal.
//
// Two-stage UX inside one modal:
//   1. Form  — Name, Email, Phone (optional) → required before generating.
//   2. Done  — Shows Report ID + Download button + email-sent status.
//
// Generation is gated by email (per product brief). No payment gate.
// Phone is optional and capped at 40 chars to avoid free-text abuse.
// ──────────────────────────────────────────────────────────────────
export default function GenerateReportModal({ open, onClose }) {
    const ctx = useHomeIQ();
    const [stage, setStage] = useState("form"); // form | working | done
    const [stageLabel, setStageLabel] = useState(null);
    const [form, setForm] = useState({ name: "", email: "", phone: "" });
    const [result, setResult] = useState(null);
    const [err, setErr] = useState(null);

    // Walk through progress messages while the backend renders. Cleared as
    // soon as generation completes or errors.
    useEffect(() => {
        if (stage !== "working") return undefined;
        const timers = REPORT_STAGES.map((s) =>
            window.setTimeout(() => setStageLabel(s.label), s.at)
        );
        setStageLabel(REPORT_STAGES[0].label);
        return () => timers.forEach((t) => window.clearTimeout(t));
    }, [stage]);

    const reset = () => {
        setStage("form");
        setStageLabel(null);
        setForm({ name: "", email: "", phone: "" });
        setResult(null);
        setErr(null);
    };
    const close = () => { reset(); onClose(); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErr(null);
        if (!form.name.trim() || !/.+@.+\..+/.test(form.email.trim())) {
            setErr("Name and a valid email are required.");
            return;
        }
        setStage("working");
        try {
            const snapshot = buildSnapshot(ctx);
            const res = await generateReport({
                lead: {
                    name: form.name.trim(),
                    email: form.email.trim(),
                    phone: form.phone.trim() || undefined,
                },
                snapshot,
            });
            setResult(res);
            setStage("done");
        } catch (ex) {
            setErr(ex.message);
            setStage("form");
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 z-[60] bg-[#0A1F44]/55 backdrop-blur-sm"
                        onClick={stage === "working" ? undefined : close}
                        data-testid="report-modal-backdrop"
                    />
                    <motion.div
                        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 16 }}
                        transition={{ duration: 0.35, ease: EASE }}
                        role="dialog" aria-modal="true" aria-label="Generate HomeIQ Report"
                        data-testid="report-modal-wrapper"
                        // Wrapper positions the modal above vertical centre on tall
                        // screens (10vh top padding on desktop) so all fields + the
                        // Generate CTA are visible on standard 1366×768 laptop
                        // viewports without scrolling. On short viewports the
                        // wrapper enables scroll; the modal card itself never
                        // overflows past 90vh and its body scrolls internally.
                        className="fixed inset-0 z-[61] flex justify-center items-start overflow-y-auto py-[6vh] sm:py-[8vh] px-4 pointer-events-none"
                    >
                        <div
                            data-testid="report-modal"
                            className="pointer-events-auto w-full max-w-md bg-white rounded-3xl shadow-2xl relative flex flex-col max-h-[calc(100vh-4rem)] overflow-hidden"
                        >
                        {stage !== "working" && (
                            <button
                                type="button" onClick={close}
                                data-testid="report-modal-close"
                                aria-label="Close report modal"
                                className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full border border-[#0A1F44]/10 flex items-center justify-center text-[#0A1F44]/60 hover:bg-[#0A1F44]/5 bg-white/90 backdrop-blur-sm"
                            >✕</button>
                        )}

                        {stage === "form" && (
                            <form onSubmit={handleSubmit} className="p-7 overflow-y-auto" data-testid="report-modal-form">
                                <div className="text-[10px] tracking-[0.28em] uppercase text-[#1E6BFF] font-semibold">— HomeIQ Report</div>
                                <h2 className="font-display font-extrabold text-2xl text-[#0A1F44] mt-1.5">Generate your curated 10-page PDF.</h2>
                                <p className="mt-2 text-sm text-[#0A1F44]/65 leading-relaxed">
                                    A focused executive read — affordability, loan, market, investment, and (if run) Vastu — wrapped into one advisor-grade document.
                                </p>

                                <div className="mt-5 space-y-3">
                                    <Field
                                        label="Full name *"
                                        testid="report-form-name"
                                        value={form.name}
                                        onChange={(v) => setForm((f) => ({ ...f, name: v }))}
                                        placeholder="As it should appear on the cover"
                                    />
                                    <Field
                                        label="Email *"
                                        testid="report-form-email"
                                        type="email"
                                        value={form.email}
                                        onChange={(v) => setForm((f) => ({ ...f, email: v }))}
                                        placeholder="We'll email the PDF here"
                                    />
                                    <Field
                                        label="Phone (optional)"
                                        testid="report-form-phone"
                                        value={form.phone}
                                        onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
                                        placeholder="+91…"
                                    />
                                </div>

                                {err && (
                                    <div data-testid="report-form-error" className="mt-4 rounded-xl bg-[#dc2626]/8 border border-[#dc2626]/25 text-[#dc2626] text-xs p-3">
                                        {err}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    data-testid="report-form-submit"
                                    className="mt-5 w-full px-5 py-3 rounded-full bg-[#0A1F44] hover:bg-[#1E6BFF] text-white text-sm font-semibold transition-colors"
                                >
                                    Generate report →
                                </button>
                                <p className="mt-3 text-[10px] text-[#0A1F44]/50 text-center">
                                    By generating, you agree to receive an email with the PDF attached.
                                </p>
                            </form>
                        )}

                        {stage === "working" && (
                            <div className="p-10 text-center" data-testid="report-modal-working">
                                <div className="w-14 h-14 mx-auto rounded-full border-4 border-[#0A1F44]/15 border-t-[#1E6BFF] animate-spin" />
                                <div className="mt-6 text-[10px] tracking-[0.28em] uppercase text-[#1E6BFF] font-semibold">
                                    — In progress
                                </div>
                                <div
                                    data-testid="report-modal-stage-label"
                                    className="mt-1.5 font-display font-extrabold text-xl text-[#0A1F44] min-h-[1.75rem]"
                                >
                                    <AnimatePresence mode="wait">
                                        <motion.span
                                            key={stageLabel}
                                            initial={{ opacity: 0, y: 6 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -6 }}
                                            transition={{ duration: 0.35, ease: EASE }}
                                            className="inline-block"
                                        >
                                            {stageLabel || "Assembling your snapshot"}…
                                        </motion.span>
                                    </AnimatePresence>
                                </div>
                                <p className="mt-3 text-sm text-[#0A1F44]/60 leading-relaxed">
                                    Composing a curated 10-page advisory PDF from your snapshot.
                                    <br />
                                    Usually takes about 20 seconds.
                                </p>
                            </div>
                        )}

                        {stage === "done" && result && (
                            <div className="p-7 overflow-y-auto" data-testid="report-modal-done">
                                <div className="text-[10px] tracking-[0.28em] uppercase text-[#16a34a] font-semibold">— Report ready</div>
                                <h2 className="font-display font-extrabold text-2xl text-[#0A1F44] mt-1.5">All set, {form.name.split(" ")[0]}.</h2>
                                <p className="mt-2 text-sm text-[#0A1F44]/65">
                                    Your HomeIQ Score came in at <strong>{result.homeiq_score} / 100</strong>.
                                </p>

                                <div className="mt-5 rounded-2xl border border-[#0A1F44]/10 bg-[#F7F8FB] p-4">
                                    <div className="text-[10px] tracking-[0.2em] uppercase text-[#0A1F44]/55 font-semibold">Report ID</div>
                                    <div data-testid="report-modal-report-id" className="mt-1 font-mono text-xs text-[#0A1F44] break-all">{result.report_id}</div>
                                </div>

                                <div className="mt-3 text-xs">
                                    {result.email_status === "sent" && (
                                        <span data-testid="report-modal-email-sent" className="text-[#16a34a] font-semibold">✓ Emailed to {form.email}</span>
                                    )}
                                    {result.email_status === "skipped" && (
                                        <span data-testid="report-modal-email-skipped" className="text-[#0A1F44]/55">Email delivery not configured — download the PDF below.</span>
                                    )}
                                    {result.email_status === "failed" && (
                                        <span data-testid="report-modal-email-failed" className="text-[#d97706]">Email send failed — download the PDF below.</span>
                                    )}
                                </div>

                                <div className="mt-5 flex flex-col sm:flex-row gap-2">
                                    <a
                                        href={reportPdfUrl(result.report_id)}
                                        target="_blank" rel="noreferrer"
                                        download
                                        data-testid="report-modal-download"
                                        className="flex-1 px-5 py-3 rounded-full bg-[#0A1F44] hover:bg-[#1E6BFF] text-white text-sm font-semibold text-center transition-colors"
                                    >
                                        Download PDF
                                    </a>
                                    <button
                                        type="button"
                                        onClick={close}
                                        data-testid="report-modal-close-done"
                                        className="px-5 py-3 rounded-full border border-[#0A1F44]/15 hover:border-[#1E6BFF]/40 text-[#0A1F44] text-sm font-semibold"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        )}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}

function Field({ label, testid, value, onChange, placeholder, type = "text" }) {
    return (
        <label className="block">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#0A1F44]/55 font-semibold mb-1.5">{label}</div>
            <input
                data-testid={testid}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full px-3 py-2.5 rounded-xl border border-[#0A1F44]/12 bg-[#F7F8FB] text-sm text-[#0A1F44] placeholder:text-[#0A1F44]/35 focus:outline-none focus:border-[#1E6BFF]/40"
            />
        </label>
    );
}
